// Module 11 — map legend as dock-panel content (was static markup in index.html). Pure DOM via the
// UiDocument seam; swatch colours use the optional setAttribute("style") (guarded so the headless test
// fake, which may omit it, never throws). Hexes mirror src/viz/effects/palette.ts so legend == map.
import type { UiDocument, UiElement } from "./seam";

interface Swatch { hex?: string; text: string }
interface Card { title: string; rows: Swatch[]; note?: string }

const CARDS: Card[] = [
  { title: "Voltage tiers (cables)", rows: [
    { hex: "#332288", text: "EHV — 230/500 kV bulk" },
    { hex: "#E69F00", text: "HV — 115–138 kV" },
    { hex: "#56B4E9", text: "ST — 44–69 kV sub-transmission" },
    { hex: "#009E73", text: "MV — 20–30 kV distribution" },
    { hex: "#CC79A7", text: "LV — <20 kV" },
  ] },
  { title: "Line loading (heat) + flow", rows: [
    { hex: "#009E73", text: "Lightly loaded — shows its base voltage-tier colour" },
    { hex: "#E69F00", text: "Loading up (≥40%) — warms toward amber" },
    { hex: "#FF8C00", text: "Heavily loaded (≥70%) — orange" },
    { hex: "#D55E00", text: "Near/over its limit (≥85%) — red, glowing" },
    { hex: "#555555", text: "De-energized or switched open — grey, no flow" },
  ], note: "A line WARMS as it carries more power; the flow arrows GROW with MW. Cut a parallel corridor and watch the survivor heat up as it takes the load." },
  { title: "Plant fuels (towers)", rows: [
    { hex: "#332288", text: "Nuclear" }, { hex: "#0072B2", text: "Hydro" }, { hex: "#E69F00", text: "Gas" },
    { hex: "#009E73", text: "Wind / bioenergy" }, { hex: "#F0E442", text: "Solar" },
    { hex: "#56B4E9", text: "Battery" }, { hex: "#999999", text: "Other" },
  ], note: "Idle plants faint; retired/decommissioned very faint grey." },
  { title: "Asset shapes (nodes)", rows: [
    { text: "▲ Generator   ■ Substation   ◆ Switch" },
    { text: "⬡ Transformer   ● Junction   📍 Consumer/Prosumer" },
  ] },
  { title: "Parcel status (walls + fills)", rows: [
    { hex: "#009E73", text: "Normal" }, { hex: "#F0E442", text: "Stressed (pulsing)" },
    { hex: "#56B4E9", text: "Shed (hatched/dim)" }, { hex: "#555555", text: "Dark / blackout" },
  ] },
  { title: "Overlay flows (ballistic arcs)", rows: [
    { hex: "#33C9FF", text: "Information — “i” arrows" },
    { hex: "#FFD54F", text: "Finance: commodity → generators — “$”" },
    { hex: "#E69F00", text: "Finance: transmission → Hydro One — “$”" },
    { hex: "#26C6DA", text: "Finance: distribution → LDC — “$”" },
  ], note: "Energy = chevrons on cables; info/finance arch above. Arrow size ∝ flow. Selected = bright ring." },
];

export function buildLegend(doc: UiDocument, host: UiElement): void {
  const cards: UiElement[] = [];
  for (const card of CARDS) {
    const c = doc.createElement("div");
    c.className = "legend-card";
    const h = doc.createElement("div");
    h.className = "legend-h";
    h.textContent = card.title;
    const rows: UiElement[] = [h];
    for (const r of card.rows) {
      const row = doc.createElement("div");
      row.className = "legend-row";
      if (r.hex) {
        const sw = doc.createElement("span");
        sw.className = "legend-sw";
        sw.setAttribute?.("style", `background:${r.hex}`);
        const label = doc.createElement("span");
        label.textContent = r.text;
        row.append(sw, label);
      } else {
        row.textContent = r.text;
      }
      rows.push(row);
    }
    if (card.note) {
      const note = doc.createElement("div");
      note.className = "legend-note";
      note.textContent = card.note;
      rows.push(note);
    }
    c.replaceChildren(...rows);
    cards.push(c);
  }
  host.replaceChildren(...cards);
}
